var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useDeepCompareEffect, useMount } from 'react-use';
import { useDispatch } from 'react-redux';
import { isRecordEmpty, removeUndefinedValues } from 'hx-util';
import { useDebouncedState } from 'hx-util';
/**
 * Used to update the store with a forms values when the form changes.
 *
 * @remarks
 * Only use when one way updating is intended. Otherwise use {@link useSyncFormWithStore}.
 *
 * @param form The form to update
 * @param updateAction The action to dispatch to update the form
 * @param extraWatchFields This is needed because react-hook-form doesn't watch nested fields properly, so they need to be specified for now
 *
 * @example
 * import { useStoreUpdater } from 'hx-wizard';
 *
 * export const [form, updateForm] = createSimpleStore('form');
 *
 * const form = useForm();
 * useStoreUpdater(form, updateForm);
 *
 */
export var useStoreUpdater = function (form, updateAction, extraWatchFields) {
    var watch = form.watch, getValues = form.getValues;
    var valueChanges = watch();
    var extraValueChanges = extraWatchFields ? watch(extraWatchFields) : {};
    var dispatch = useDispatch();
    var debouncedValueChanges = useDebouncedState(valueChanges, 200);
    var debouncedExtraValueChanges = useDebouncedState(extraValueChanges);
    var mergedValueChanges = __assign(__assign({}, debouncedValueChanges), debouncedExtraValueChanges);
    useMount(function () { return dispatch(updateAction(getValues())); });
    useDeepCompareEffect(function () {
        var newValue = removeUndefinedValues(mergedValueChanges);
        if (isRecordEmpty(newValue))
            return;
        dispatch(updateAction(newValue));
    }, [mergedValueChanges]);
};
export default useStoreUpdater;
